/**
 * WBRChatbox — floating-overlay chat interface embedded in the WBR Callouts
 * Viewer.
 *
 * Task 5.1 built the *presentational* component and the open/collapsed overlay
 * state. Task 6.1 (this revision) wires the async `askAI2` mutation: the send
 * handler validates input, dispatches `SUBMIT`, clears the input, builds the
 * mutation input, and races the `client.graphql` call against a 30s timeout
 * (Req 6.2) with a 60s pending-state ceiling backstop (Req 5.5), mapping the
 * result to `SUCCESS` / `EMPTY_RESPONSE` / `FAILURE` / `TIMEOUT` dispatches. It
 * also adds an auto-scroll effect on message append (Req 8.3) and a team-change
 * effect that resets the conversation when `currentTeamId` changes (Req 7.5).
 *
 * Conversation state is owned by the pure `chatReducer`; the open/collapsed flag
 * is local presentation-only UI state (`isOpen`) and is deliberately kept out of
 * `ChatState` (see design "Open / collapsed UI state").
 */

import React, { useEffect, useReducer, useRef, useState } from "react";
import {
  Box,
  Chip,
  Fab,
  IconButton,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MinimizeIcon from "@mui/icons-material/Minimize";
import SendIcon from "@mui/icons-material/Send";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TuneIcon from "@mui/icons-material/Tune";
import { marked, parse } from "marked";
import { Resizable } from "re-resizable";
import katex from "katex";
import "katex/dist/katex.min.css";
import { submitQuestion2 } from "../../graphql/mutations";
import {
  getConversation,
  getUserKnowledgeSummary,
} from "../../graphql/queries";
import { GetUserKnowledgeInput, UserKnowledgeSummary } from "../../models/API";
import { useGlobal } from "../../context/GlobalContext";
import { hexToString } from "../utils/utils";
import {
  extractErrorMarker,
  StoredResponseError,
} from "../utils/responseMarkers";
import AnswerTableDownloads from "../csvDownload/AnswerTableDownloads";
import ResourceSelector from "../ResourceSelector";
import PdfUploadButton from "../PdfUploadButton";
import {
  chatReducer,
  initialState,
  ChatMessage,
  Source,
} from "./chatbox/chatReducer";
import {
  buildAskAI2Input,
  computeSendEnabled,
  isValidId,
  parseSources,
  shouldShowPlaceholder,
  validateQuestion,
} from "./chatbox/chatHelpers";
import {
  ACCENT_ORANGE,
  IDENTITY_UNAVAILABLE_TEXT,
  MAX_SOURCES,
  NAVY,
  OVERLAY_Z_INDEX,
} from "./chatbox/chatConstants";

/**
 * What the user is currently looking at in the WBR Callouts Viewer. Passed to
 * the backend so answers ground to the on-screen week/report/page instead of
 * falling back to the latest week. Read at send time (see `viewContextRef`).
 */
export interface WbrViewContext {
  /** "YYYY-Www" — the selected week. */
  week: string;
  /** Human label, e.g. "WK10 · Mar 2, 2026" (prompt readability). */
  weekLabel?: string;
  /** Selected report filename; omitted in summary/merged views. */
  reportName?: string;
  /** Which view is active. */
  view: "summary" | "merged" | "report";
  /** 1-based page number, when in single-report view. */
  pageNum?: number;
}

/** A bounded, on-screen callout projection sent alongside the view context. */
export interface WbrViewCallout {
  title: string;
  status?: string;
  why?: string;
}

export interface WBRChatboxProps {
  /** From `userConfig.id` — numeric user identity. */
  currentUserId: number | null | undefined;
  /** From the viewer's `currentTeamId` prop. */
  currentTeamId: number | null | undefined;
  /** From the viewer's `currentUserName` prop. */
  currentUserName: string;
  /** Live "what am I viewing" context from the viewer (read at send time). */
  viewContext?: WbrViewContext | null;
  /** Bounded projection of the callouts currently on screen. */
  viewCallouts?: WbrViewCallout[] | null;
  /**
   * Stable Amplify GraphQL client (the viewer memoizes `generateClient()`).
   * Typed structurally to avoid the deep instantiation blowup of
   * `ReturnType<typeof generateClient>`; we only ever call `.graphql(...)`.
   */
  client: {
    graphql: (options: { query: string; variables?: unknown }) => unknown;
  };
}

// Render assistant replies as GitHub-flavored markdown with single-newline
// line breaks, mirroring the existing ChatBoxComponent. The backend
// (`ask_ai_2`) returns markdown (bold, lists, headings); without this they
// render as literal `**` / `1.` text.
marked.setOptions({ breaks: true, gfm: true });

/** Escape HTML so raw LaTeX can be shown as a safe text fallback. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Build a placeholder span carrying URI-encoded LaTeX for later KaTeX render.
 *  The raw LaTeX is also kept as visible fallback text so the formula is never
 *  blank if KaTeX fails to load. */
function mathPlaceholder(latex: string, displayMode: boolean): string {
  const encoded = encodeURIComponent(latex);
  return `<span class="wbr-math" data-display="${
    displayMode ? "1" : "0"
  }" data-latex="${encoded}">${escapeHtml(latex.trim())}</span>`;
}

/**
 * Render assistant markdown to HTML, extracting LaTeX math *before* markdown
 * processing so formula contents (subscripts `_`, `*`, etc.) aren't mangled by
 * marked. Supports `$$...$$` and `\[...\]` (display) and `\(...\)` (inline).
 * Bare single-`$` is intentionally NOT treated as math so currency amounts
 * (e.g. "$1.2M") render literally.
 */
function renderMarkdownWithMath(text: string): string {
  let work = text;
  work = work.replace(/\$\$([\s\S]+?)\$\$/g, (_m, tex) =>
    mathPlaceholder(tex, true)
  );
  work = work.replace(/\\\[([\s\S]+?)\\\]/g, (_m, tex) =>
    mathPlaceholder(tex, true)
  );
  work = work.replace(/\\\(([\s\S]+?)\\\)/g, (_m, tex) =>
    mathPlaceholder(tex, false)
  );
  const html = parse(work) as string;
  // Wrap tables in a horizontal-scroll container so wide (5+ column) tables
  // scroll instead of squeezing/wrapping cell text (e.g. "Germ an y").
  return html
    .replace(/>\s+</g, "><")
    .replace(/<table>/g, '<div class="wbr-table-scroll"><table>')
    .replace(/<\/table>/g, "</table></div>");
}

/** Fixed card dimensions for the floating overlay (Open_State). */
const PANEL_WIDTH = 416;
const PANEL_DEFAULT_HEIGHT = 560;
const PANEL_MIN_WIDTH = 360;
const PANEL_MIN_HEIGHT = 380;
/** Bottom-right anchor offsets, clear of the viewer header controls (Req 8.8). */
const ANCHOR_OFFSET = 24;

// ── Theme accents derived from the viewer palette ─────────────────────────
const NAVY_DARK = "#1A2330";
const HEADER_GRADIENT = `linear-gradient(135deg, ${NAVY} 0%, #2E4257 100%)`;
const USER_BUBBLE_GRADIENT = `linear-gradient(135deg, ${NAVY} 0%, #35495F 100%)`;
const SEND_GRADIENT = `linear-gradient(135deg, ${ACCENT_ORANGE} 0%, #FFB23E 100%)`;
const SURFACE = "#F7F9FC";

/**
 * Scoped styles for the chatbox: custom scrollbar, entrance + typing-dot
 * animations, and rich-markdown rendering (tables scroll horizontally instead
 * of wrapping mid-word, styled code/quotes/headings, tidy KaTeX spacing).
 */
const CHATBOX_STYLES = `
.wbr-scroll::-webkit-scrollbar { width: 8px; }
.wbr-scroll::-webkit-scrollbar-track { background: transparent; }
.wbr-scroll::-webkit-scrollbar-thumb { background: rgba(35,47,62,0.16); border-radius: 8px; }
.wbr-scroll::-webkit-scrollbar-thumb:hover { background: rgba(35,47,62,0.30); }
@keyframes wbrFadeInUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes wbrDot { 0%, 80%, 100% { transform: translateY(0); opacity: .35; } 40% { transform: translateY(-4px); opacity: 1; } }
.wbr-md { font-size: 14px; line-height: 1.6; color: #1F2937; }
.wbr-md > :first-child { margin-top: 0; }
.wbr-md > :last-child { margin-bottom: 0; }
.wbr-md p { margin: 0 0 8px; }
.wbr-md ul, .wbr-md ol { margin: 0 0 8px; padding-left: 20px; }
.wbr-md li { margin-bottom: 3px; }
.wbr-md h1, .wbr-md h2, .wbr-md h3, .wbr-md h4 { margin: 12px 0 6px; font-size: 14px; font-weight: 700; color: #0F1B2D; }
.wbr-md a { color: #0A66C2; text-decoration: underline; }
.wbr-md strong { color: #0F1B2D; }
.wbr-md code { background: #EEF1F5; border-radius: 4px; padding: 1px 5px; font-size: 12.5px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.wbr-md pre { background: ${NAVY_DARK}; color: #E8EEF5; border-radius: 10px; padding: 10px 12px; overflow-x: auto; margin: 0 0 8px; }
.wbr-md pre code { background: transparent; color: inherit; padding: 0; }
.wbr-md blockquote { margin: 0 0 8px; padding: 6px 12px; border-left: 3px solid ${ACCENT_ORANGE}; background: #FFF7EC; color: #475569; border-radius: 0 6px 6px 0; }
.wbr-md hr { border: none; border-top: 1px solid #E5E7EB; margin: 12px 0; }
.wbr-md .wbr-table-scroll { overflow-x: auto; max-width: 100%; margin: 2px 0 8px; border: 1px solid #E2E8F0; border-radius: 10px; }
.wbr-md table { border-collapse: separate; border-spacing: 0; width: auto; min-width: 100%; font-size: 12.5px; }
.wbr-md thead th { background: #EEF2F7; color: #0F1B2D; font-weight: 700; white-space: nowrap; text-align: left; padding: 8px 11px; border-bottom: 1px solid #E2E8F0; }
.wbr-md tbody td { padding: 8px 11px; white-space: nowrap; border-bottom: 1px solid #EEF1F5; color: #243140; }
.wbr-md tbody tr:nth-of-type(even) { background: #FAFBFD; }
.wbr-md tbody tr:last-child td { border-bottom: none; }
.wbr-md .katex-display { margin: 10px 0; overflow-x: auto; overflow-y: hidden; padding: 2px 0; }
`;

/**
 * Polling cadence for the async `submitQuestion2` path. After submitting, the
 * answer is produced asynchronously by the SubmitQuestion2 Lambda and read back
 * by polling `getConversation` until the turn's response appears.
 */
const POLL_INTERVAL_MS = 2000;
/** Fallback total poll budget (seconds) when the global timeout is unavailable. */
const DEFAULT_POLL_BUDGET_SEC = 120;

/** Markers the backend embeds in the saved response for the frontend to parse. */
const SOURCES_MARKER = /\n<!-- SOURCES:([\s\S]*?) -->/;
const TOOLCALLS_MARKER = /\n<!-- TOOLCALLS:([\s\S]*) -->/;

/**
 * Decode a hex-encoded stored response into display text plus parsed sources.
 * Strips the hidden SOURCES / TOOLCALLS / ERROR markers the backend appends;
 * `error` is set when the stored turn is a failure rather than an answer.
 */
function decodeStoredResponse(hexResponse: string): {
  text: string;
  sources: Source[];
  error?: StoredResponseError;
} {
  const decoded = extractErrorMarker(hexToString(hexResponse));
  const error = decoded.error;
  let text = decoded.text;
  let sources: Source[] = [];
  const sourcesMatch = text.match(SOURCES_MARKER);
  if (sourcesMatch) {
    text = text.replace(sourcesMatch[0], "");
    sources = parseSources(sourcesMatch[1]);
  }
  const toolCallsMatch = text.match(TOOLCALLS_MARKER);
  if (toolCallsMatch) {
    text = text.replace(toolCallsMatch[0], "");
  }
  return { text, sources, error };
}

/**
 * Generate a stable client-owned conversation id. The client mints this once
 * per conversation and reuses it for every turn so the backend groups turns
 * together and loads prior history (matching the Ask AI panel's approach).
 */
function generateConversationId(): string {
  const cryptoObj =
    typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

/**
 * Render a single source reference as a compact chip (Req 4.4).
 */
const SourceItem: React.FC<{ label: string; detail?: string }> = ({
  label,
  detail,
}) => (
  <Chip
    size="small"
    label={detail ? `${label} · ${detail}` : label}
    sx={{
      maxWidth: "100%",
      height: 22,
      fontSize: 11,
      fontWeight: 500,
      color: NAVY,
      backgroundColor: "#EEF2F7",
      border: "1px solid #E2E8F0",
      "& .MuiChip-label": {
        px: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    }}
  />
);

/**
 * Render a single chat message bubble with a role label and opposite
 * horizontal alignment for user vs. assistant (Req 4.3). Assistant messages
 * may carry parsed source references, rendered as distinct items capped at
 * `MAX_SOURCES` (Req 4.4).
 */
const MessageBubble: React.FC<{
  message: ChatMessage;
  /** Zero-based position of this message in the conversation (Req 4.1). */
  messageIndex: number;
  /** Conversation identifier, null until the first turn assigns one (Req 4.4). */
  conversationId?: string | null;
}> = ({ message, messageIndex, conversationId }) => {
  const isUser = message.role === "user";
  const sources = (message.sources ?? []).slice(0, MAX_SOURCES);
  // Render assistant answers (not error notices) as formatted markdown; keep
  // user messages and error/timeout notices as plain text.
  const renderMarkdown = !isUser && !message.isError;
  const html = React.useMemo(
    () => (renderMarkdown ? renderMarkdownWithMath(message.text) : ""),
    [renderMarkdown, message.text]
  );
  // The element holding the injected answer HTML. `AnswerTableDownloads` owns
  // that element now and hands it back through this ref, which it assigns in a
  // callback ref during commit — so it is populated before this component's
  // effects run, same as when the span carried the ref directly.
  const mathRef = useRef<HTMLElement | null>(null);

  // Typeset the LaTeX placeholders with KaTeX once the HTML is in the DOM.
  useEffect(() => {
    const el = mathRef.current;
    if (!el) {
      return;
    }
    el.querySelectorAll<HTMLElement>(".wbr-math").forEach((node) => {
      if (node.getAttribute("data-rendered") === "1") {
        return;
      }
      const latex = decodeURIComponent(node.getAttribute("data-latex") || "");
      const displayMode = node.getAttribute("data-display") === "1";
      try {
        katex.render(latex, node, { displayMode, throwOnError: false });
        node.setAttribute("data-rendered", "1");
      } catch {
        node.textContent = displayMode ? `$$${latex}$$` : `\\(${latex}\\)`;
      }
    });
  }, [html]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isUser ? "row-reverse" : "row",
        alignItems: "flex-start",
        gap: 1,
        mb: 1.5,
        animation: "wbrFadeInUp 0.25s ease both",
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 0.25,
          color: "#FFFFFF",
          background: isUser
            ? USER_BUBBLE_GRADIENT
            : message.isError
            ? "#FCA5A5"
            : SEND_GRADIENT,
          boxShadow: "0 1px 3px rgba(15,27,45,0.18)",
        }}
      >
        {isUser ? (
          <Typography sx={{ fontSize: 11, fontWeight: 700 }}>You</Typography>
        ) : (
          <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
        )}
      </Box>

      {/* Bubble */}
      <Box
        sx={{
          maxWidth: "82%",
          minWidth: 0,
          px: 1.5,
          py: 1.1,
          borderRadius: 2.5,
          borderTopRightRadius: isUser ? 4 : 20,
          borderTopLeftRadius: isUser ? 20 : 4,
          whiteSpace: renderMarkdown ? "normal" : "pre-wrap",
          wordBreak: "break-word",
          fontSize: 14,
          lineHeight: 1.55,
          color: isUser ? "#FFFFFF" : message.isError ? "#B91C1C" : "#1F2937",
          background: isUser
            ? USER_BUBBLE_GRADIENT
            : message.isError
            ? "#FEF2F2"
            : "#FFFFFF",
          border: message.isError
            ? "1px solid #FCA5A5"
            : isUser
            ? "none"
            : "1px solid #E7ECF2",
          boxShadow: isUser
            ? "0 2px 8px rgba(15,27,45,0.20)"
            : "0 1px 3px rgba(15,27,45,0.06)",
        }}
      >
        {renderMarkdown ? (
          // The answer plus one "Download CSV" control per table it contains
          // (Req 1.7, 2.1). `tableWrapperSelector` points at the horizontal
          // scroll box this surface wraps every table in, so each control lands
          // after that box rather than inside it (Req 2.2).
          <AnswerTableDownloads
            html={html}
            messageIndex={messageIndex}
            conversationId={conversationId}
            className="wbr-md"
            hostRef={mathRef}
            tableWrapperSelector=".wbr-table-scroll"
          />
        ) : (
          message.text
        )}
        {sources.length > 0 ? (
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: "1px dashed #E2E8F0",
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                color: "#94A3B8",
                mr: 0.25,
              }}
            >
              Sources
            </Typography>
            {sources.map((source, idx) => (
              <SourceItem
                key={`${message.id}-source-${idx}`}
                label={source.label}
                detail={source.detail}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

/**
 * The WBR Callouts Viewer chatbox.
 *
 * Owns the conversation via `useReducer(chatReducer, initialState)` and the
 * open/collapsed overlay state via `useState<boolean>`. When collapsed, only
 * the launcher (`Chatbox_Toggle_Control`) is rendered; when open, the floating
 * card is rendered with the conversation history, placeholder/pending states,
 * the question input, send, new-conversation, and collapse controls.
 */
const WBRChatbox: React.FC<WBRChatboxProps> = ({
  currentUserId,
  currentTeamId,
  currentUserName,
  viewContext,
  viewCallouts,
  client,
}) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  // App-wide request timeout (seconds), used as the polling budget; and the
  // data-source-selector visibility preference (shared with the Ask AI panel).
  const { globalTimeout, showDataSourceSelector } = useGlobal();
  // Presentation-only open/collapsed flag — intentionally NOT in ChatState so
  // the conversation persists across collapse/expand (Req 8.9, 8.11).
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  // Resource picker + PDF attach state (parity with the Ask AI panel). The
  // data-source selection is shared with that panel via ResourceSelector's
  // localStorage defaults. `null` means "not yet known" → omit data_source_ids
  // so the backend uses its default (all tools).
  const [selectedDataSourceIds, setSelectedDataSourceIds] = useState<
    number[] | null
  >(null);
  const [pdfS3Key, setPdfS3Key] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string | null>(null);
  // Instruction skills (parity with the Ask AI panel): available skills are
  // user_knowledge rows of type "skill"; selected names are sent as `skills`.
  const [availableSkills, setAvailableSkills] = useState<
    UserKnowledgeSummary[]
  >([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  // Anchor for the data-source selector popover (shown when the setting is on).
  const [dsAnchorEl, setDsAnchorEl] = useState<HTMLElement | null>(null);
  // Deterministic, monotonically increasing client ids for messages.
  const messageIdCounter = useRef(0);
  // Bottom sentinel used to auto-scroll the newest message into view (Req 8.3).
  const bottomRef = useRef<HTMLDivElement | null>(null);
  // Live view context, mirrored into refs so the async send handler reads the
  // CURRENT selection at send time rather than a value captured in its closure
  // (a stale closure would send the wrong week/report).
  const viewContextRef = useRef<WbrViewContext | null | undefined>(viewContext);
  const viewCalloutsRef = useRef<WbrViewCallout[] | null | undefined>(
    viewCallouts
  );
  useEffect(() => {
    viewContextRef.current = viewContext;
    viewCalloutsRef.current = viewCallouts;
  }, [viewContext, viewCallouts]);

  const identityValid = isValidId(currentUserId);
  const sendEnabled = computeSendEnabled({
    status: state.status,
    userId: currentUserId,
    teamId: currentTeamId,
    input: inputValue,
  });

  const nextMessageId = (): string => {
    messageIdCounter.current += 1;
    return `msg-${messageIdCounter.current}`;
  };

  // ── Fetch instruction skills (parity with the Ask AI panel) ───────────────
  // Skills are user_knowledge rows with knowledge_type === "skill" for the
  // current user/team. Selected skill names are sent with each question.
  // Metadata only (no content): a whole team's content in one response
  // crossed the RDS Data API 1 MiB cap, which emptied the picker for that team.
  useEffect(() => {
    if (!isValidId(currentUserId) || !isValidId(currentTeamId)) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const input: GetUserKnowledgeInput = {
          user_id: currentUserId,
          team_id: currentTeamId,
        };
        const result = (await client.graphql({
          query: getUserKnowledgeSummary,
          variables: { input },
        })) as {
          data?: { getUserKnowledge?: UserKnowledgeSummary[] | null };
        };
        if (cancelled) {
          return;
        }
        const all = result?.data?.getUserKnowledge ?? [];
        setAvailableSkills(all.filter((k) => k.knowledge_type === "skill"));
      } catch {
        if (!cancelled) setAvailableSkills([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId, currentTeamId, client]);

  // ── Auto-scroll: bring the newest message fully into view on append ───────
  // Keyed on the message count so each appended Chat_Message scrolls the
  // history to the bottom sentinel (Req 8.3). Also runs on the pending-state
  // transition so the "Thinking…" indicator stays visible.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "nearest" });
  }, [state.messages.length, state.status]);

  // ── Team-change reset: clear the conversation when the team changes ───────
  // When the viewer's `currentTeamId` differs from the team the active
  // conversation is bound to, reset the conversation and rebind (Req 7.5). This
  // also performs the initial bind (boundTeamId starts null).
  useEffect(() => {
    if (isValidId(currentTeamId) && currentTeamId !== state.boundTeamId) {
      dispatch({ type: "TEAM_CHANGED", teamId: currentTeamId });
    }
  }, [currentTeamId, state.boundTeamId]);

  // ── Pending-state ceiling backstop ────────────────────────────────────────
  // The async poll loop is bounded by the global timeout and always dispatches
  // a terminal action, so no separate ceiling timer is needed here.

  /**
   * Send handler (task 6.1): validate, submit, and invoke the `askAI2`
   * mutation.
   *
   * On a submittable question it appends the user Chat_Message, clears the
   * input (Req 2.3, 2.4), builds the mutation input (Req 2.2), and races the
   * `client.graphql` call against a 30s timeout (Req 6.2). Results map to
   * dispatches: `success` + non-empty `response` → `SUCCESS` (parsing sources
   * and storing a non-empty `conversation_id`, Req 3.2/3.3/4.1); `success` +
   * empty `response` → `EMPTY_RESPONSE` (Req 4.5); `success === false` →
   * `FAILURE` with the server message (Req 6.3/6.4); a rejection → `FAILURE`
   * default (Req 6.1); a timeout → `TIMEOUT` (Req 6.2). The reducer returns
   * `status` to idle on every terminal action, re-enabling send (Req 5.3, 5.4,
   * 6.6).
   */
  const handleSend = async (): Promise<void> => {
    const validation = validateQuestion(inputValue);
    if (validation.kind !== "ok" || !sendEnabled) {
      // Empty/whitespace/too-long or gated by identity/pending → no-op (Req 2.5).
      return;
    }
    // Narrow the identities to valid positive integers (also gates the call).
    if (!isValidId(currentUserId) || !isValidId(currentTeamId)) {
      return;
    }

    const question = validation.trimmed;
    // Mint a stable conversation id on the first turn and reuse it for every
    // follow-up so the backend keeps multi-turn context (it keeps whatever id
    // we send; it only generates one when none is provided).
    const conversationId = state.conversationId ?? generateConversationId();
    // Append the user message and transition to pending before the call.
    dispatch({
      type: "SUBMIT",
      messageId: nextMessageId(),
      text: question,
      conversationId,
    });
    setInputValue("");

    const input = buildAskAI2Input({
      question,
      userId: currentUserId,
      teamId: currentTeamId,
      userName: currentUserName,
      conversationId,
      // Match the Ask AI panel: only scope data sources when the selector is
      // enabled; otherwise omit (= backend loads all tools).
      dataSourceIds: showDataSourceSelector ? selectedDataSourceIds : null,
      skills: selectedSkills,
      pdfS3Key,
      pdfFilename,
      // Read from refs so we send the CURRENT view, not the mount-time value.
      viewContext: viewContextRef.current ?? undefined,
      viewCallouts: viewCalloutsRef.current ?? undefined,
    });
    // The attached PDF is consumed by this question; clear it after sending.
    setPdfS3Key(null);
    setPdfFilename(null);

    const replyId = nextMessageId();
    try {
      // 1. Submit the question asynchronously (SQS-backed). Returns quickly with
      //    a question_id; the answer is produced by the SubmitQuestion2 Lambda.
      const submitResult = (await client.graphql({
        query: submitQuestion2,
        variables: { input },
      })) as {
        data?: {
          submitQuestion2?: {
            success: boolean;
            message: string;
            question_id: string;
          } | null;
        };
      };
      const payload = submitResult?.data?.submitQuestion2 ?? null;

      if (!payload || payload.success !== true) {
        // Submission rejected: show the server message, else default.
        dispatch({
          type: "FAILURE",
          messageId: replyId,
          message: payload?.message ?? "",
        });
        return;
      }

      // 2. Poll getConversation until the answer for this question_id appears,
      //    bounded by the global timeout budget.
      await pollForAnswer(payload.question_id, conversationId, replyId);
    } catch (err) {
      // Network / GraphQL error submitting the question (Req 6.1).
      dispatch({ type: "FAILURE", messageId: replyId, message: "" });
    }
  };

  /**
   * Poll `getConversation` (keyed by the returned `questionId`) every
   * `POLL_INTERVAL_MS` until the turn's response is available or the budget is
   * exhausted. Dispatches `SUCCESS` (with decoded text + parsed sources),
   * `EMPTY_RESPONSE`, or `TIMEOUT`.
   */
  const pollForAnswer = async (
    questionId: string,
    conversationId: string,
    replyId: string
  ): Promise<void> => {
    const budgetSec =
      typeof globalTimeout === "number" && globalTimeout > 0
        ? globalTimeout
        : DEFAULT_POLL_BUDGET_SEC;
    const maxAttempts = Math.max(
      1,
      Math.ceil((budgetSec * 1000) / POLL_INTERVAL_MS)
    );

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const result = (await client.graphql({
          query: getConversation,
          variables: {
            input: { user_id: currentUserId, conversation_id: questionId },
          },
        })) as {
          data?: {
            getConversation?: Array<{
              response?: string | null;
            }> | null;
          };
        };
        const rows = result?.data?.getConversation ?? [];
        const hexResponse = rows.length > 0 ? rows[0].response : null;
        if (!hexResponse) {
          // Answer not ready yet; keep polling.
          continue;
        }
        const { text, sources, error } = decodeStoredResponse(hexResponse);
        if (error) {
          // A failed turn: show the backend's message as an error notice.
          dispatch({ type: "FAILURE", messageId: replyId, message: text });
        } else if (text.trim().length > 0) {
          dispatch({
            type: "SUCCESS",
            messageId: replyId,
            response: text,
            conversationId,
            sources,
          });
        } else {
          dispatch({ type: "EMPTY_RESPONSE", messageId: replyId });
        }
        return;
      } catch (err) {
        // Transient polling error — keep trying until the budget runs out.
        continue;
      }
    }
    // Budget exhausted without an answer (Req 6.2 timeout semantics).
    dispatch({ type: "TIMEOUT", messageId: replyId });
  };

  const handleNewConversation = (): void => {
    dispatch({ type: "NEW_CONVERSATION" });
    setInputValue("");
  };

  const handleInputKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    // Enter submits; Shift+Enter inserts a newline.
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  // ── Collapsed_State: render only the launcher (Req 1.6, 8.10) ─────────────
  if (!isOpen) {
    const hasConversation = state.messages.length > 0;
    return (
      <Tooltip
        title={hasConversation ? "Resume WBR chat" : "Ask about this WBR"}
        placement="left"
      >
        <Fab
          aria-label={hasConversation ? "Resume WBR chat" : "Open WBR chat"}
          onClick={() => setIsOpen(true)}
          sx={{
            position: "fixed",
            bottom: ANCHOR_OFFSET,
            right: ANCHOR_OFFSET,
            zIndex: OVERLAY_Z_INDEX,
            color: "#FFFFFF",
            background: HEADER_GRADIENT,
            boxShadow: "0 6px 18px rgba(15,27,45,0.32)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
              background: HEADER_GRADIENT,
              transform: "translateY(-2px) scale(1.04)",
              boxShadow: "0 10px 24px rgba(15,27,45,0.40)",
            },
          }}
        >
          <AutoAwesomeIcon sx={{ color: ACCENT_ORANGE }} />
        </Fab>
      </Tooltip>
    );
  }

  // ── Open_State: render the floating panel (Req 1.2, 1.5) ──────────────────
  return (
    <Resizable
      defaultSize={{ width: PANEL_WIDTH, height: PANEL_DEFAULT_HEIGHT }}
      minWidth={PANEL_MIN_WIDTH}
      minHeight={PANEL_MIN_HEIGHT}
      maxWidth={
        typeof window !== "undefined" ? window.innerWidth - 32 : undefined
      }
      maxHeight={
        typeof window !== "undefined" ? window.innerHeight - 32 : undefined
      }
      enable={{
        top: true,
        left: true,
        topLeft: true,
        right: false,
        bottom: false,
        bottomRight: false,
        bottomLeft: false,
        topRight: false,
      }}
      handleStyles={{
        topLeft: {
          width: 16,
          height: 16,
          left: 2,
          top: 2,
        },
      }}
      style={{
        position: "fixed",
        bottom: ANCHOR_OFFSET,
        right: ANCHOR_OFFSET,
        zIndex: OVERLAY_Z_INDEX,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          height: "100%",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(15,27,45,0.08)",
          boxShadow: "0 18px 48px rgba(15,27,45,0.28)",
        }}
      >
        <style>{CHATBOX_STYLES}</style>
        {/* Header: bot avatar, title + subtitle, new-conversation, minimize. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: HEADER_GRADIENT,
            color: "#FFFFFF",
            px: 1.5,
            py: 1.25,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: SEND_GRADIENT,
              color: "#FFFFFF",
              flexShrink: 0,
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
              WBR Assistant
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <Tooltip title="New conversation">
              <span>
                <IconButton
                  size="small"
                  aria-label="Start new conversation"
                  onClick={handleNewConversation}
                  disabled={state.messages.length === 0}
                  sx={{
                    color: ACCENT_ORANGE,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
                  }}
                >
                  <AddCommentOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Minimize (keeps this conversation)">
              <IconButton
                size="small"
                aria-label="Minimize WBR chat"
                onClick={() => setIsOpen(false)}
                sx={{
                  color: "#FFFFFF",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                }}
              >
                <MinimizeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Conversation history — the only scrollable region (Req 8.2). */}
        <Box
          className="wbr-scroll"
          sx={{
            flex: 1,
            minHeight: 200,
            overflowY: "auto",
            px: 1.5,
            py: 1.75,
            background: SURFACE,
          }}
        >
          {!identityValid ? (
            // Identity unavailable notice (Req 7.2).
            <Typography
              sx={{
                fontSize: 13,
                color: "#B91C1C",
                backgroundColor: "#FEF2F2",
                border: "1px solid #FCA5A5",
                borderRadius: 1,
                p: 1.25,
              }}
            >
              {IDENTITY_UNAVAILABLE_TEXT}
            </Typography>
          ) : shouldShowPlaceholder(state) ? (
            // Minimal empty state — icon + short heading only.
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                mt: 4,
                px: 1,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: SEND_GRADIENT,
                  color: "#FFFFFF",
                  mb: 1.5,
                  boxShadow: "0 6px 16px rgba(255,153,0,0.35)",
                }}
              >
                <AutoAwesomeIcon />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: NAVY }}>
                How can I help?
              </Typography>
            </Box>
          ) : (
            state.messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                messageIndex={index}
                conversationId={state.conversationId}
              />
            ))
          )}

          {/* Typing indicator (Req 5.1). */}
          {state.status === "pending" ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mb: 1.5,
                animation: "wbrFadeInUp 0.25s ease both",
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 0.25,
                  color: "#FFFFFF",
                  background: SEND_GRADIENT,
                  boxShadow: "0 1px 3px rgba(15,27,45,0.18)",
                }}
              >
                <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2.5,
                  borderTopLeftRadius: 4,
                  background: "#FFFFFF",
                  border: "1px solid #E7ECF2",
                  boxShadow: "0 1px 3px rgba(15,27,45,0.06)",
                }}
              >
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: ACCENT_ORANGE,
                      animation: "wbrDot 1.2s infinite ease-in-out",
                      animationDelay: `${i * 0.16}s`,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : null}

          {/* Bottom sentinel — auto-scroll target for the newest message (Req 8.3). */}
          <div ref={bottomRef} />
        </Box>

        {/* Composer: resource picker + PDF attach + question input (pinned). */}
        <Box
          sx={{
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#FAFAFA",
            flexShrink: 0,
          }}
        >
          {/* Skill selector chips (parity with the Ask AI panel). */}
          {identityValid && availableSkills.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                px: 1.25,
                pt: 1.25,
              }}
            >
              {availableSkills.map((skill) => {
                const active = selectedSkills.includes(skill.knowledge_name);
                return (
                  <Chip
                    key={skill.id}
                    label={skill.knowledge_name}
                    size="small"
                    variant={active ? "filled" : "outlined"}
                    onClick={() =>
                      setSelectedSkills((prev) =>
                        prev.includes(skill.knowledge_name)
                          ? prev.filter((s) => s !== skill.knowledge_name)
                          : [...prev, skill.knowledge_name]
                      )
                    }
                    sx={{
                      cursor: "pointer",
                      fontSize: 11.5,
                      height: 24,
                      ...(active
                        ? { backgroundColor: NAVY, color: "#FFFFFF" }
                        : { color: NAVY, borderColor: "#CBD5E1" }),
                    }}
                  />
                );
              })}
            </Box>
          ) : null}

          {/* Question input row — PDF attach + text + send. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 1.1,
            }}
          >
            {identityValid ? (
              <PdfUploadButton
                userId={currentUserId as number}
                onPdfUploaded={(key, filename) => {
                  setPdfS3Key(key);
                  setPdfFilename(filename);
                }}
                onPdfRemoved={() => {
                  setPdfS3Key(null);
                  setPdfFilename(null);
                }}
                disabled={state.status === "pending"}
              />
            ) : null}
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Ask a question…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={!identityValid}
              inputProps={{ "aria-label": "Question input" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 5,
                  backgroundColor: "#FFFFFF",
                  fontSize: 14,
                  "& fieldset": { borderColor: "#E2E8F0" },
                  "&:hover fieldset": { borderColor: "#CBD5E1" },
                  "&.Mui-focused fieldset": {
                    borderColor: ACCENT_ORANGE,
                    borderWidth: 1.5,
                  },
                },
              }}
            />
            {identityValid && showDataSourceSelector ? (
              <Tooltip title="Data sources">
                <IconButton
                  aria-label="Select data sources"
                  onClick={(e) => setDsAnchorEl(e.currentTarget)}
                  disabled={state.status === "pending"}
                  sx={{ flexShrink: 0, color: NAVY }}
                >
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title="Send">
              <span>
                <IconButton
                  aria-label="Send question"
                  onClick={handleSend}
                  disabled={!sendEnabled}
                  sx={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    color: "#FFFFFF",
                    background: SEND_GRADIENT,
                    boxShadow: "0 2px 8px rgba(255,153,0,0.35)",
                    transition: "transform 0.12s ease, opacity 0.12s ease",
                    "&:hover": {
                      background: SEND_GRADIENT,
                      transform: "scale(1.06)",
                    },
                    "&.Mui-disabled": {
                      background: "#E2E8F0",
                      color: "#FFFFFF",
                      boxShadow: "none",
                    },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Data-source selector popover (parity with the Ask AI panel). */}
          <Popover
            open={Boolean(dsAnchorEl)}
            anchorEl={dsAnchorEl}
            onClose={() => setDsAnchorEl(null)}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 2, minWidth: 300 }}>
              {identityValid ? (
                <ResourceSelector
                  userId={currentUserId as number}
                  onSelectionChange={(_knowledgeIds, dataSourceIds) =>
                    setSelectedDataSourceIds(dataSourceIds)
                  }
                />
              ) : null}
            </Box>
          </Popover>
        </Box>
      </Paper>
    </Resizable>
  );
};

export default WBRChatbox;
